import { EditorMergeImage, EditorMergeParams } from './editor-domain.models';
import { Size2D } from './interfaces/shared';
import { MergeTileLayout, resolveMergeLayout, toMergeDrawRect } from './merge-layout';

export interface MergeCanvasPreview {
  url: string;
  width: number;
  height: number;
  tiles: MergeTileLayout[];
}

export interface MergeArrangeTile {
  id: string;
  surface: HTMLImageElement;
  x: number;
  y: number;
  borderWidth: number;
  borderColor: string | null;
}

const IMAGE_CACHE_LIMIT = 32;

/**
 * Composes the live merge preview on a canvas, replicating the backend
 * single-page merger pixel semantics: every tile is drawn stretched into its
 * border-inflated rectangle and the border is stroked on top of it.
 */
export class MergePreviewComposer {
  private readonly imageCache = new Map<string, Promise<HTMLImageElement>>();
  private composeVersion = 0;

  async measureImageSizes(images: readonly EditorMergeImage[]): Promise<Size2D[]> {
    const elements = await Promise.all(images.map(image => this.loadImage(image.previewUrl)));
    return elements.map(element => ({
      width: Math.max(1, element.naturalWidth || element.width || 1),
      height: Math.max(1, element.naturalHeight || element.height || 1),
    }));
  }

  /** Loaded image elements with stored positions — input of the drag-arrange canvas mode. */
  async resolveArrangeTiles(params: EditorMergeParams): Promise<MergeArrangeTile[]> {
    const elements = await Promise.all(
      params.images.map(image => this.loadImage(image.previewUrl)),
    );
    const borderWidth = Math.max(0, Math.round(params.borderWidth || 0));

    return params.images.map((image, index) => ({
      id: image.id,
      surface: elements[index],
      x: Math.max(0, Math.round(image.x || 0)),
      y: Math.max(0, Math.round(image.y || 0)),
      borderWidth,
      borderColor: borderWidth > 0 ? params.borderColor : null,
    }));
  }

  /** Resolves with null when a newer compose call supersedes this one. */
  async compose(params: EditorMergeParams): Promise<MergeCanvasPreview | null> {
    const version = ++this.composeVersion;
    const elements = await Promise.all(
      params.images.map(image => this.loadImage(image.previewUrl)),
    );

    if (version !== this.composeVersion) {
      return null;
    }

    const sizes = elements.map(element => ({
      width: Math.max(1, element.naturalWidth || element.width || 1),
      height: Math.max(1, element.naturalHeight || element.height || 1),
    }));
    const layout = resolveMergeLayout(params, sizes);
    const canvas = document.createElement('canvas');
    canvas.width = layout.width;
    canvas.height = layout.height;
    const context = canvas.getContext('2d');

    if (!context) {
      return null;
    }

    if (params.backgroundColor) {
      context.fillStyle = params.backgroundColor;
      context.fillRect(0, 0, canvas.width, canvas.height);
    }

    const borderWidth = Math.max(0, Math.round(params.borderWidth || 0));

    for (const tile of layout.tiles) {
      const drawRect = toMergeDrawRect(tile.bounds, borderWidth);
      context.drawImage(
        elements[tile.imageIndex],
        drawRect.x,
        drawRect.y,
        drawRect.width,
        drawRect.height,
      );

      if (borderWidth > 0 && params.borderColor) {
        context.strokeStyle = params.borderColor;
        context.lineWidth = borderWidth;
        context.strokeRect(drawRect.x, drawRect.y, drawRect.width, drawRect.height);
      }
    }

    return {
      url: canvas.toDataURL('image/png'),
      width: layout.width,
      height: layout.height,
      tiles: layout.tiles,
    };
  }

  dispose(): void {
    this.composeVersion += 1;
    this.imageCache.clear();
  }

  private loadImage(url: string): Promise<HTMLImageElement> {
    const cached = this.imageCache.get(url);
    if (cached) {
      // refresh the LRU position
      this.imageCache.delete(url);
      this.imageCache.set(url, cached);
      return cached;
    }

    const loading = new Promise<HTMLImageElement>((resolve, reject) => {
      const element = new Image();
      element.onload = () => resolve(element);
      element.onerror = () => {
        this.imageCache.delete(url);
        reject(new Error('Failed to load a merge preview image.'));
      };
      element.src = url;
    });

    this.imageCache.set(url, loading);
    while (this.imageCache.size > IMAGE_CACHE_LIMIT) {
      const oldestUrl = this.imageCache.keys().next().value as string;
      this.imageCache.delete(oldestUrl);
    }

    return loading;
  }
}
